<template>
  <div class="flex flex-col h-full flex-1 bg-gray-900">
    <div class="flex-1 flex flex-col pb-4 overflow-y-auto pt-8">
      <RouterLink
        v-if="$currentAccount.logo"
        :to="{ name: 'accounts.show', params: { accountId: $currentAccount.id } }"
      >
        <img :src="$currentAccount.logo" class="block rounded w-24 mb-8 mx-auto" />
      </RouterLink>
      <h6 class="text-center text-white mb-6">
        {{ $currentAccount.name }}
      </h6>
      <nav class="flex-1 bg-gray-900" aria-label="Sidebar">
        <template v-for="(item, routeIdx) in navigation">
          <SidebarNavigationGroup
            v-if="'children' in item"
            :key="`root_item_${routeIdx}`"
            :label="item.label"
            :children="item.children"
          />
          <SidebarNavigationLink
            v-else
            :key="`root_item_${routeIdx}`"
            :route="item.route"
            :label="item.label"
            :exact="item.exact"
            :tip="item.tip"
          />
        </template>
      </nav>
    </div>
  </div>
</template>

<script>
import SidebarNavigationLink from './SidebarNavigationLink.vue';
import SidebarNavigationGroup from './SidebarNavigationGroup.vue';

const availableRoutes = [
  { name: 'accounts.show', params: {}, permissions: [] },
  { name: 'accounts.calendar', params: {}, permissions: ['read contacts'] },
  { name: 'accounts.accountId.prospects', params: {}, permissions: [] },
  { name: 'accounts.accountId.salesFunnel.contacts', params: {}, permissions: [] },
  { name: 'accounts.accountId.customers', params: {}, permissions: [] },
  { name: 'accounts.accountId.contacts', params: {}, permissions: ['read contacts'] },
  { name: 'accounts.accountId.contactMerge', params: {}, permissions: ['edit contacts'] },
  {
    name: 'accounts.accountId.contactRequests',
    params: {},
    permissions: ['read contact requests'],
  },
  { name: 'accounts.pipelines', params: {}, permissions: ['read pipelines'] },
  { name: 'accounts.projects', params: {}, permissions: ['read projects'] },
  { name: 'accounts.channels', params: {}, permissions: ['read channels'] },
  { name: 'accounts.campaigns', params: {}, permissions: ['read campaigns'] },
  { name: 'accounts.accountId.audiences', params: {}, permissions: ['read audiences'] },
  { name: 'accounts.accountId.funnels', params: {}, permissions: ['read funnels'] },
  { name: 'accounts.accountId.emailCampaigns', params: {}, permissions: ['read email campaigns'] },
  { name: 'accounts.accountId.smsCampaigns', params: {}, permissions: ['read sms campaigns'] },
  { name: 'accounts.accountId.accOrders', params: {}, permissions: ['read acc orders'] },
  { name: 'accounts.accountId.clientProducts', params: {}, permissions: ['read acc products'] },
  { name: 'accounts.accountId.assets', params: {}, permissions: ['read assets'] },
  { name: 'accounts.pages', params: {}, permissions: ['read pages'] },
  { name: 'accounts.accountId.emailTemplates', params: {}, permissions: ['read email templates'] },
  { name: 'accounts.accountId.marketingDashboard', params: {}, permissions: [] },
];

const groups = {
  reports: {
    label: 'Reports',
    translationKey: 'accountSidebarD.reportsGroup.label',
  },
  contactManagement: {
    label: 'C.R.M. Contact Management',
    translationKey: 'accountSidebarD.contactManagementGroup.label',
  },
  marketing: {
    label: 'Marketing',
    translationKey: 'accountSidebarD.labels.marketingGroup',
  },
  sales: {
    label: 'Sales',
    translationKey: 'accountSidebarD.labels.salesGroup',
  },
};

const availableNavigationItems = [
  {
    label: 'Home',
    translationKey: 'accountSidebarD.labels.homeLink',
    route: 'accounts.show',
    group: null,
    exact: true,
  },
  {
    label: 'Social media',
    translationKey: 'accountSidebarD.reportsGroup.links.socialMedia',
    route: 'accounts.accountId.prospects',
    group: 'reports',
  },
  {
    label: 'Contacts',
    translationKey: 'accountSidebarD.reportsGroup.links.contacts',
    route: 'accounts.accountId.salesFunnel.contacts',
    group: 'reports',
  },
  {
    label: 'Customers',
    translationKey: 'accountSidebarD.reportsGroup.links.customers',
    route: 'accounts.accountId.customers',
    group: 'reports',
  },
  {
    label: 'Contacts',
    translationKey: 'accountSidebarD.contactManagementGroup.links.contacts',
    route: 'accounts.accountId.contacts',
    group: 'contactManagement',
  },
  {
    label: 'Contact requests',
    translationKey: 'accountSidebarD.contactManagementGroup.links.contactRequests',
    route: 'accounts.accountId.contactRequests',
    group: 'contactManagement',
  },
  {
    label: 'Pipelines',
    translationKey: 'accountSidebarD.contactManagementGroup.links.pipelines',
    route: 'accounts.pipelines',
    group: 'contactManagement',
  },
  {
    label: 'Calendar',
    translationKey: 'accountSidebarD.contactManagementGroup.links.calendar',
    route: 'accounts.calendar',
    group: 'contactManagement',
  },
  {
    label: 'Manage duplicates',
    translationKey: 'accountSidebarD.contactManagementGroup.links.manageDuplicates',
    route: 'accounts.accountId.contactMerge',
    group: 'contactManagement',
  },
  {
    label: 'Dashboard',
    translationKey: 'shared.dashboard',
    route: 'accounts.accountId.marketingDashboard',
    group: 'marketing',
  },
  {
    label: 'Projects',
    translationKey: 'accountSidebarD.marketingGroup.links.projects',
    route: 'accounts.projects',
    group: 'marketing',
  },
  {
    label: 'Webpages',
    translationKey: 'accountSidebarD.marketingGroup.links.webpages',
    route: 'accounts.pages',
    group: 'marketing',
  },
  {
    label: 'Ads',
    translationKey: 'accountSidebarD.marketingGroup.links.ads',
    route: 'accounts.channels',
    group: 'marketing',
  },
  {
    label: 'Ad plans',
    translationKey: 'accountSidebarD.marketingGroup.links.adPlans',
    route: 'accounts.campaigns',
    group: 'marketing',
  },
  {
    label: 'Assets',
    translationKey: 'accountSidebarD.marketingGroup.links.assets',
    route: 'accounts.accountId.assets',
    group: 'marketing',
  },
  {
    label: 'Templates',
    translationKey: 'accountSidebarD.marketingGroup.links.templates',
    route: 'accounts.accountId.emailTemplates',
    group: 'marketing',
  },
  {
    label: 'Audiences',
    translationKey: 'accountSidebarD.salesGroup.links.audiences',
    route: 'accounts.accountId.audiences',
    group: 'sales',
  },
  {
    label: 'Funnels',
    translationKey: 'accountSidebarD.salesGroup.links.funnels',
    route: 'accounts.accountId.funnels',
    group: 'sales',
  },
  {
    label: 'Orders',
    translationKey: 'accountSidebarD.salesGroup.links.orders',
    route: 'accounts.accountId.accOrders',
    group: 'sales',
  },
  {
    label: 'Products',
    translationKey: 'accountSidebarD.salesGroup.links.products',
    route: 'accounts.accountId.clientProducts',
    group: 'sales',
  },
];

const navigationSchema = [
  { type: 'link', name: 'accounts.show' },
  { type: 'group', name: 'reports' },
  { type: 'group', name: 'contactManagement' },
  { type: 'group', name: 'marketing' },
  { type: 'group', name: 'sales' },
];

export default {
  components: {
    SidebarNavigationLink,
    SidebarNavigationGroup,
  },
  computed: {
    navigation() {
      const navigationItems = availableNavigationItems
        .filter((navigationItem) => {
          const route = availableRoutes.find((item) => item.name === navigationItem.route);

          if (!route) {
            return false;
          }

          if (
            typeof navigationItem.group === 'string' &&
            navigationItem.group in groups === false
          ) {
            return false;
          }

          if (!route.permissions) {
            return true;
          }

          if (route.permissions.length === 0) {
            return true;
          }

          return this.$membershipHasPermissionTo(route.permissions);
        })
        .reduce((navigation, navigationItem) => {
          const route = availableRoutes.find((item) => item.name === navigationItem.route);

          return [
            ...navigation,
            {
              label: this.$te(navigationItem.translationKey)
                ? this.$t(navigationItem.translationKey)
                : navigationItem.label,
              route: {
                name: navigationItem.route,
                params: {
                  ...route.params,
                  accountId: this.$currentAccount.id,
                },
              },
              tip: this.$isAdminOrSuperAdmin
                ? route.permissions.length
                  ? `Required permissions: ${route.permissions.join(', ')}.`
                  : 'No permissions required.'
                : '',
              exact: !!navigationItem.exact,
              group: navigationItem.group,
            },
          ];
        }, []);

      return navigationSchema
        .map((item) => {
          if (item.type !== 'link' && item.type !== 'group') {
            return null;
          }

          if (item.type === 'link') {
            return navigationItems.find(
              (navigationItem) =>
                navigationItem.group === null && navigationItem.route.name === item.name
            );
          }

          if (item.name in groups === false) {
            return null;
          }

          const children = navigationItems.filter(
            (navigationItem) => navigationItem.group === item.name
          );

          if (children.length === 0) {
            return null;
          }

          const { [item.name]: group } = groups;
          return {
            label: this.$te(group.translationKey) ? this.$t(group.translationKey) : group.label,
            children,
          };
        })
        .filter(Boolean);
    },
  },
  methods: {
    translate(keyword, fallback = null) {
      const te = this.$te.bind(this);
      const t = this.$t.bind(this);
      if (te(keyword)) {
        return t(keyword);
      }

      return fallback || keyword;
    },
  },
};
</script>
